// Claude: split from adminAPI.js — buyers, buyer subscriptions, rewards, reward plans, buyer assets 14-04-2026
// Claude: getCreditBalance + getStatements endpoints; addBuyerPaymentRecord new payload 25-Apr-2026
import { api } from "./config/axiosConfig.js";
import { store } from "../redux/Store.ts";
import { responseHandler } from "../Utils/handler.ts";

export const AdminBuyerAPI = {

  // Buyers
  postBuyer: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/buyers/",
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  getBuyer: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/buyers/",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params: params,
    });
    return response.data;
  },

  getEventBuyer: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/event-buyers/",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params: params,
    });
    return response.data;
  },

  getBuyerById: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${id}/`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getBuyersByEventId: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/events/${id}/buyers/`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: get all eventIds for a buyer — admin multi-event UI 06-03-2026
  getBuyerEventIds: async (buyerId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/events`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  putBuyer: async (id, data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${id}`,
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  // Claude: check if another verified buyer shares same full email domain 21-03-2026
  checkBuyerDomainConflict: async (buyerId) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/domain-verified-check`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin override buyer verification status 26-02-2026
  overrideBuyerVerification: async (buyerId, status, reason) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/verification`,
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      data: { status, reason },
    });
    return response.data;
  },

  deleteBuyer: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: bulk delete — single request instead of N parallel requests 24-04-2026
  bulkDeleteBuyers: async (ids) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/bulk`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
      data: { ids },
    });
    return response.data;
  },

  // Claude: unlink buyer from one event only 07-Apr-2026
  unlinkBuyerFromEvent: async (buyerId, eventId) => {
    const { token } = store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/event/${eventId}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin adds payment record → writes to buyer_statements + increments buyerRFXLimits 25-Apr-2026
  // payload: { date, paymentReference, planName, planAmount, creditsUtilized, paidAmount, rfxAdded }
  addBuyerPaymentRecord: async (buyerId, data) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/add-payment-record`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response.data;
  },

  // Claude: admin gets current credit balance for a buyer — from buyer_statements 25-Apr-2026
  getCreditBalance: async (buyerId) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/credit-balance`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin gets all statement records for a buyer 25-Apr-2026
  getStatements: async (buyerId) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/statements`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: admin adjusts credits after billing 20-Apr-2026
  adjustBuyerCredits: async (buyerId, data) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/adjust-credits`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response.data;
  },

  // Claude: admin renews buyer subscription — optional plan change, resets per-cycle counters 21-04-2026
  renewBuyerSubscription: async (buyerId, data = {}) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/admin/buyers/${buyerId}/renew-subscription`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data,
    });
    return response.data;
  },

  // Buyer Subscriptions — Claude: 11-Apr-2026
  getBuyerSubscriptions: async (cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/buyer-subscriptions",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  addBuyerSubscriptionPlan: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/buyer-subscriptions`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  updateBuyerSubscriptionPlan: async (id, data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/buyer-subscriptions/${id}`,
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  getBuyerSubscriptionUsage: async (id) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/buyer-subscriptions/${id}/usage`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  deleteBuyerSubscription: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/buyer-subscriptions/${id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: buyer sends renewal request → email to admin 11-Apr-2026
  requestBuyerSubscriptionPlan: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/buyer-subscriptions/sendEmail`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  // Rewards
  getRewardsByBuyerId: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `admin/buyer-rewards/statements`,
      params,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getRedemptionsSummary: async (eventId, buyerId, cancel = false) => {
    const { token } = await store.getState().user;
    const params = { eventId, buyerId };
    try {
      const response = await api.request({
        url: `admin/buyer-redemptions/redemptions-summary`,
        params,
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data ?? null;
    } catch (error) {
      console.error("Error fetching redemptions summary:", error);
      if (error.response && error.response.status === 404) return null;
      throw error;
    }
  },

  getRedemptions: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `admin/buyer-redemptions/redemption`,
      params,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getLatestRedemptionByRRID: async (params, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `admin/buyer-redemptions/redemptionid`,
      params,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateRedemption: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `admin/buyer-redemptions/updateredemption`,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  // Buyer Digital Assets
  getBuyerAssets: async (buyerId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `buyers/digital-assets/${buyerId}`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  addBuyerAsset: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "buyers/digital-assets",
      method: "POST",
      data: data,
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  deleteBuyerAsset: async (assetId, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "buyers/digital-assets/" + assetId,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return responseHandler(response);
  },

  // Reward Plans — Claude: 27-02-2026
  getRewardPlans: async (cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/reward-plans",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  addRewardPlan: async (data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/reward-plans",
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  updateRewardPlan: async (id, data, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/reward-plans/${id}`,
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      data: data,
    });
    return response.data;
  },

  deleteRewardPlan: async (id, cancel = false) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: `/reward-plans/${id}`,
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Claude: CSV export — triggers browser file download 27-02-2026
  exportBuyers: async (params) => {
    const { token } = await store.getState().user;
    const response = await api.request({
      url: "/admin/buyers/export",
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      params,
      responseType: "blob",
    });
    const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 15);
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `buyers_${ts}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

};
